import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus } from "lucide-react";

import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

import { API } from "../components/customers/constants";
import { useBatchActions } from "../components/customers/useBatchActions";
import { CustomerFilters } from "../components/customers/CustomerFilters";
import { BatchActionToolbar } from "../components/customers/BatchActionToolbar";
import { CustomerTable } from "../components/customers/CustomerTable";
import { CustomerPagination } from "../components/customers/CustomerPagination";

export default function Customers() {
  const { user } = useAuth();
  const canMutate = ["owner", "admin", "superadmin"].includes(user?.role);
  const canScreen = ["owner", "admin", "reviewer", "superadmin"].includes(user?.role);

  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [kycFilter, setKycFilter] = useState(searchParams.get("kyc_status") || "all");
  const [riskFilter, setRiskFilter] = useState(searchParams.get("risk_level") || "all");
  const [personTypeFilter, setPersonTypeFilter] = useState(searchParams.get("person_type") || "all");
  // Cliente state filter:
  //   "all"    → finalized customers + drafts (default)
  //   "active" → only finalized customers (no drafts)
  //   "draft"  → only drafts (incomplete onboardings)
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "all");
  const [loading, setLoading] = useState(true);

  // Selection is tracked by id (customer_id OR draft_id) in a Set so it
  // survives pagination and filter changes. Drafts ARE selectable now —
  // useful so the operator can bulk-delete abandoned onboardings — but
  // they are excluded from screening/PDF/export actions automatically
  // because those operations don't make sense on incomplete records.
  const [selected, setSelected] = useState(() => new Set());

  // IDs on the current page.
  const allRowIds = useMemo(() => customers.map((c) => c.customer_id), [customers]);
  const selectableIds = allRowIds;
  const selectedCount = selected.size;
  const draftSelectedCount = useMemo(
    () => customers.filter((c) => c.is_draft && selected.has(c.customer_id)).length,
    [customers, selected],
  );
  const finalizedSelectedCount = selectedCount - draftSelectedCount;

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (kycFilter !== "all") params.kyc_status = kycFilter;
      if (riskFilter !== "all") params.risk_level = riskFilter;
      if (personTypeFilter !== "all") params.person_type = personTypeFilter;
      if (stateFilter !== "all") params.state = stateFilter;
      const res = await axios.get(`${API}/customers`, { params, withCredentials: true });
      setCustomers(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, kycFilter, riskFilter, personTypeFilter, stateFilter]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // ── Single-row delete (inline trash button) ───────────────────────
  const handleDelete = async (customer, e) => {
    e.preventDefault();
    e.stopPropagation();

    const isDraft = !!customer.is_draft;
    const confirmMsg = isDraft
      ? "¿Eliminar este borrador? Perderás el progreso no guardado."
      : "¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.";
    if (!window.confirm(confirmMsg)) return;

    try {
      const url = isDraft
        ? `${API}/customer-drafts/${customer.draft_id || customer.customer_id}`
        : `${API}/customers/${customer.customer_id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(isDraft ? "Borrador eliminado" : "Cliente eliminado exitosamente");
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  // ── Multi-select helpers ──────────────────────────────────────────
  const toggleOne = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };
  const allVisibleSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) selectableIds.forEach((id) => next.delete(id));
      else selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const selectedArray = useMemo(() => Array.from(selected), [selected]);
  // For actions that don't apply to drafts (screening, PDFs, CSV export),
  // we silently filter out draft IDs at call-time. The UI also disables the
  // buttons in that case to make the constraint explicit.
  const finalizedSelectedArray = useMemo(
    () => selectedArray.filter((id) => !id.startsWith("draft_")),
    [selectedArray],
  );

  const onBatchSuccess = useCallback(() => {
    clearSelection();
    fetchCustomers();
  }, [fetchCustomers]);

  const {
    busyAction, batchProgress,
    runScreening, downloadPdfs, exportCsv, deleteBatch,
  } = useBatchActions({
    selectedArray,
    finalizedSelectedArray,
    onSuccess: onBatchSuccess,
  });

  return (
    <Layout>
      <div data-testid="customers-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="customers-heading">Clientes (KYC)</h1>
            <p className="text-slate-400 mt-1">{total} clientes registrados</p>
          </div>
          <Link to="/customers/new">
            <Button className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="new-customer-button">
              <Plus size={16} className="mr-2" /> Nuevo Cliente
            </Button>
          </Link>
        </div>

        <CustomerFilters
          search={search} setSearch={setSearch}
          kycFilter={kycFilter} setKycFilter={setKycFilter}
          riskFilter={riskFilter} setRiskFilter={setRiskFilter}
          personTypeFilter={personTypeFilter} setPersonTypeFilter={setPersonTypeFilter}
          stateFilter={stateFilter} setStateFilter={setStateFilter}
          setPage={setPage}
        />

        <BatchActionToolbar
          selectedCount={selectedCount}
          draftSelectedCount={draftSelectedCount}
          finalizedSelectedCount={finalizedSelectedCount}
          busyAction={busyAction}
          batchProgress={batchProgress}
          canScreen={canScreen}
          canMutate={canMutate}
          onRunScreening={canScreen ? runScreening : undefined}
          onDownloadPdfs={downloadPdfs}
          onExportCsv={exportCsv}
          onDeleteBatch={canMutate ? deleteBatch : undefined}
          onClear={clearSelection}
        />

        <CustomerTable
          customers={customers}
          loading={loading}
          selected={selected}
          selectableIds={selectableIds}
          allVisibleSelected={allVisibleSelected}
          onToggleOne={toggleOne}
          onToggleAll={toggleAllVisible}
          onDelete={handleDelete}
        />

        <CustomerPagination
          page={page}
          pages={pages}
          total={total}
          onPageChange={setPage}
        />
      </div>
    </Layout>
  );
}
